/**
 * LoginView — entry page (gate).
 * Real Microsoft SSO. Both tabs use the same MSAL popup — Microsoft handles
 * first-time vs repeat users transparently. The "Create account" tab exists
 * for UX clarity, not because it does anything different.
 */
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';

export default function LoginView({ onNavigate }) {
  const { signIn } = useAuth();
  const [mode, setMode] = useState('signin');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    setError('');
    const res = await signIn();
    setBusy(false);
    if (!res.ok) {
      if (res.error) setError(res.error);
      return;
    }
    if (onNavigate) onNavigate('dashboard');
  };

  const tab = (key, label) => (
    <button
      onClick={() => {
        setMode(key);
        setError('');
      }}
      style={{
        flex: 1,
        padding: '10px 0',
        border: 0,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        background: 'transparent',
        color: mode === key ? '#0F172A' : '#94A3B8',
        borderBottom: mode === key ? '2px solid #16a34a' : '2px solid transparent',
        fontFamily: 'inherit',
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0' }}>
          {tab('signin', 'Sign in')}
          {tab('signup', 'Create account')}
        </div>

        <div style={{ padding: '32px 32px 28px' }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0F172A' }}>
            {mode === 'signin' ? 'Welcome back' : 'Get started'}
          </h1>
          <p style={{ marginTop: 6, marginBottom: 24, color: '#64748B', fontSize: 13 }}>
            {mode === 'signin'
              ? 'Sign in with your GAT Microsoft account.'
              : 'New here? Sign in with your GAT account — your GlobalOps profile is created automatically.'}
          </p>

          <button
            onClick={handleClick}
            disabled={busy}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: '#0078D4',
              color: '#fff',
              border: 0,
              borderRadius: 8,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              fontFamily: 'inherit',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
              <path fill="#f25022" d="M1 1h10v10H1z" />
              <path fill="#7fba00" d="M12 1h10v10H12z" />
              <path fill="#00a4ef" d="M1 12h10v10H1z" />
              <path fill="#ffb900" d="M12 12h10v10H12z" />
            </svg>
            {busy ? 'Opening Microsoft…' : mode === 'signin' ? 'Sign in with Microsoft' : 'Continue with Microsoft'}
          </button>

          {error && (
            <div
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 8,
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                fontSize: 12,
                color: '#B91C1C',
              }}
            >
              {error}
            </div>
          )}

          {mode === 'signup' && !error && (
            <div
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 8,
                background: '#F1F5F9',
                fontSize: 11,
                color: '#475569',
                lineHeight: 1.5,
              }}
            >
              <strong>Heads up:</strong> Your account is your GAT Microsoft account. The first time you sign in here we'll create your
              GlobalOps profile automatically — no separate password to manage.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
